import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DashboardFiltersFormData,
  DashboardFiltersFormSchema,
  DEFAULT_MEMBER_TYPE,
} from "@/modules/stochastic/features/Reports/SCDashboard/hooks/dashboardFiltersFormSchema";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FetchDashboardDataRequest } from "@/modules/stochastic/features/Reports/SCDashboard/api/fetchStochasticDashboardData/types";
import {
  fetchStochasticDashboardChartsDataAction,
  refreshStochasticDashboardDataAction,
} from "@/modules/stochastic/features/Reports/SCDashboard/slice/StochasticDashboardSlice";
import { useToast } from "@/components/ui/use-toast";
import { AppDispatch } from "@/app/store";
import { RootState } from "@/app/rootReducer";
import { selectIntacctId } from "@/modules/hub/features/UserAppSettings/selectors/userAppSettingsSelectors";
import { useSearchParams } from "react-router-dom";

export function useStochasticDashboard() {
  const dispatch = useDispatch<AppDispatch>();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const activeCompanyIntacctId = useSelector(selectIntacctId);

  // Get intacctId from URL params (when coming from the Campaign Status page) or use active company
  const intacctId =
    searchParams.get("intacctId") || activeCompanyIntacctId || "";

  const defaultValues: DashboardFiltersFormData = {
    memberType: DEFAULT_MEMBER_TYPE,
    years: [],
    cities: [],
    trades: [],
  };

  const [activeTab, setActiveTabState] = useState<
    "sales" | "customers" | "review"
  >("sales");

  const setActiveTab = useCallback((tab: "sales" | "customers" | "review") => {
    setActiveTabState(tab);
    const container = document.getElementById("main-scroll-area");
    if (container) {
      container.scrollTo({ top: 0, behavior: "auto" });
    }
  }, []);
  const [areFiltersDirty, setAreFiltersDirty] = useState(false);

  const { dashboardData, loading, refreshing, error } = useSelector(
    (state: RootState) => state.stochasticDashboard,
  );

  const dashboardFiltersForm = useForm<DashboardFiltersFormData>({
    resolver: zodResolver(DashboardFiltersFormSchema),
    defaultValues,
    mode: "onChange",
  });

  const buildRequestData = useCallback((): FetchDashboardDataRequest => {
    const formValues = dashboardFiltersForm.getValues();

    return {
      intacctId,
      memberType: (formValues.memberType?.id || DEFAULT_MEMBER_TYPE.id) as
        | "residential"
        | "commercial"
        | "both",
      years: (formValues.years || []).map((year) => year.name),
      trades: (formValues.trades || []).map((trade) => trade.id),
      cities: (formValues.cities || []).map((city) => city.name),
    };
  }, [dashboardFiltersForm, intacctId]);

  const applyFilters = useCallback(() => {
    const requestData = buildRequestData();
    dispatch(fetchStochasticDashboardChartsDataAction(requestData));
    setAreFiltersDirty(false);
  }, [dispatch, buildRequestData]);

  const handleRefresh = useCallback(async () => {
    if (!intacctId || refreshing) return;

    try {
      await dispatch(refreshStochasticDashboardDataAction(intacctId));

      toast({
        title: "Success",
        description: "Dashboard data has been refreshed.",
      });

      applyFilters();
    } catch {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to refresh dashboard data",
      });
    }
  }, [dispatch, intacctId, refreshing, toast, applyFilters]);

  useEffect(() => {
    applyFilters();
  }, []);

  useEffect(() => {
    const subscription = dashboardFiltersForm.watch(() => {
      setAreFiltersDirty(true);
    });

    return () => subscription.unsubscribe();
  }, [dashboardFiltersForm]);

  return {
    loading,
    refreshing,
    error,
    dashboardData,
    dashboardFiltersForm,
    activeTab,
    setActiveTab,
    applyFilters,
    areFiltersDirty,
    handleRefresh,
  };
}
